import { z } from "zod";
import { disableButton, enableButton, updateWorkspaceCount } from "../../lib/utility";
import { createFormFieldHandler } from "../../utils/form-handler";
import { workspaceService } from "../../services/workspaceService";
import { addToast } from "../../toasts";
import { router } from "../../router";
import { WorkspaceType } from "../../stores/workspaceStore";
import { SelectedWorkspaceCtx } from "../../contexts/SelectedWorkspaceCtx";

const LOGO_MAX_SIZE = 15 * 1024 * 1024;
const NAME_MAX_LENGTH = 128;
const DESCRIPTION_MAX_LENGTH = 160;

export function CreateWorkspaceForm({ closeParentModal }: { closeParentModal: () => void; }) {
  // Custom Zod schema for logo validation
  const logoSchema = z
    .instanceof(File)
    .optional()
    .refine((file) => !file || !file.size || (file.type.startsWith("image/") && file.type !== "image/avif"), {
      message: "Only image files are allowed",
    })
    .refine((file) => !file || !file.size || file.size <= LOGO_MAX_SIZE, {
      message: `File size must be less than ${LOGO_MAX_SIZE / 1024 / 1024} MB`,
    });

  const formSchema = z.object({
    logo: logoSchema,
    name: z.string().max(NAME_MAX_LENGTH, `Max ${NAME_MAX_LENGTH} characters allowed`).min(1, "Workspace name is required"),
    description: z.string().max(DESCRIPTION_MAX_LENGTH, `Max ${DESCRIPTION_MAX_LENGTH} characters allowed`),
    website_url: z.string().regex(/^$|^(?!https?\.)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$|^https?:\/\/[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$/).or(z.literal('')),
  });

  type FormData = z.infer<typeof formSchema>;

  let $formElem: null | HTMLFormElement = null;
  let $logoInput: null | HTMLInputElement = null;
  let $logoPreview: null | HTMLElement = null;
  let $uploadLogoButton: null | HTMLButtonElement = null;
  let $uploadLogoButtonText: null | HTMLSpanElement = null;
  let $submitButton: null | HTMLButtonElement = null;
  let $descInput: null | HTMLTextAreaElement = null;
  let $descCount: null | HTMLElement = null;

  let resetFormErrors: undefined | (() => void);

  function updateUploadButtonText(hasImage: boolean) {
    if ($uploadLogoButtonText) {
      $uploadLogoButtonText.textContent = hasImage ? "Change photo" : "Upload photo";
    }
  }

  function resetForm() {
    $formElem?.reset();
    $descCount!.innerText = "00";
    $logoPreview!.innerHTML = `<svg class="shrink-0 size-7" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
      stroke-linejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>`;
    updateUploadButtonText(false);
    resetFormErrors && resetFormErrors();
  }

  function init() {
    $formElem = document.getElementById("create-workspace-form") as HTMLFormElement;
    $submitButton = $formElem.querySelector('#btn-create-workspace-modal-save') as HTMLButtonElement;
    $logoInput = $formElem.querySelector("#logo") as HTMLInputElement;
    $logoPreview = $formElem.querySelector("#logo-preview") as HTMLElement;
    $uploadLogoButton = $formElem.querySelector("#upload-logo-button") as HTMLButtonElement;
    $uploadLogoButtonText = $formElem.querySelector("#upload-logo-button-text") as HTMLSpanElement;
    $descInput = $formElem.querySelector('[name="description"]') as HTMLTextAreaElement;
    $descCount = $formElem.querySelector(".ws-desc-count") as HTMLElement;

    if (!($formElem && $submitButton && $logoInput && $logoPreview && $uploadLogoButton && $uploadLogoButtonText && $descInput && $descCount)) {
      console.error("Can't find all the Create Workspace form elements!");
    }

    $descInput?.addEventListener("input", (e) => {
      const target = e.target as HTMLTextAreaElement;
      $descCount!.innerText = target.value.length.toString();
    });

    // Handle logo file selection and preview
    $uploadLogoButton.addEventListener("click", () => {
      $logoInput!.click();
    });

    $logoInput.addEventListener("change", () => {
      const file = $logoInput!.files?.[0];
      if (file) {
        // Validate file using Zod
        try {
          logoSchema.parse(file);

          // Show preview
          const reader = new FileReader();
          reader.onload = () => {
            $logoPreview!.innerHTML = `<img src="${reader.result}" class="w-auto h-auto max-w-[76px] max-h-[76px] object-cover " />`;
            updateUploadButtonText(true);
          };
          reader.readAsDataURL(file);
        } catch (error) {
          if (error instanceof z.ZodError) {
            setFieldError("logo", error.errors[0].message);
          }
        }
      }
    });

    const { setFieldError, validateOnBlur, resetErrors } = createFormFieldHandler(
      formSchema,
      $formElem
    );

    resetFormErrors = resetErrors;
    // Add event listeners to form fields to validate on blur
    validateOnBlur();

    // Handle form submission
    $formElem.addEventListener("submit", async (e) => {
      e.preventDefault();
      const formData = new FormData($formElem!);
      const data = Object.fromEntries(formData.entries()) as FormData;

      try {
        formSchema.parse(data);
        disableButton($submitButton!);
        try {
          const createWSResp = await workspaceService.create(formData) as { data: WorkspaceType; };

          await workspaceService.list(false);

          // update fs3_workspace_count in cookie
          updateWorkspaceCount();
          
          $formElem!.reset();
          enableButton($submitButton!);
          closeParentModal();
          SelectedWorkspaceCtx.setSelectedWorkspace(createWSResp.data);
          router.navigate("/feedbox");

        } catch (error: any) {
          // this catch block is for handling Server errors on the request
          console.error("Workspace Create request failed: ", error);
          addToast({ toastBody: error.message }, "default", "error");
          error.errors && Object.keys(error.errors).forEach((field) => {
            // Display each error message for the corresponding field
            setFieldError(field as keyof FormData, error.errors[field][0]);
          });
        }
      } catch (error) {
        // this catch block is for handling Frontend form validation
        if (error instanceof z.ZodError) {
          error.errors.forEach((err) => {
            setFieldError(err.path[0] as keyof FormData, err.message);
          });
        }
      } finally {
        enableButton($submitButton!);
      }
    });

    const hasInitialImage = $logoPreview?.querySelector('img') !== null;
    updateUploadButtonText(hasInitialImage);
  }

  function render() {
    return /*html*/`
    <form id="create-workspace-form">
      <!-- Body -->
      <div class="p-4 space-y-5">
        <div>
          <label class="block mb-2 text-sm font-medium text-gray-800 dark:text-neutral-200">
            Workspace Logo
          </label>

          <!-- Logo Upload Group -->
          <div class="flex flex-wrap items-center gap-3 sm:gap-5">
            <span id="logo-preview"
              class="flex shrink-0 p-2 overflow-hidden justify-center items-center size-20 border-2 border-dotted border-gray-300 text-gray-400 rounded-full dark:border-neutral-700 dark:text-neutral-600">
              <svg class="shrink-0 size-7" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
                stroke-linejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
            </span>

            <div class="grow">
              <div class="flex items-center gap-x-2">
                <input type="file" id="logo" name="logo" class="hidden" accept="image/*" />
                <button type="button" id="upload-logo-button"
                  class="py-2 px-3 inline-flex items-center gap-x-2 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 focus:outline-none focus:bg-gray-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700">
                  <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <span id="upload-logo-button-text">Upload Photo</span>
                </button>
              </div>
              <p class="mt-2 text-xs text-gray-500 dark:text-neutral-500">
                Pick an image, max 15MB
              </p>
              <p class="logo-error hidden text-sm text-red-600 mt-2"></p>
            </div>
          </div>
          <!-- End Logo Upload Group -->
        </div>

        <div>
          <label for="name" class="flex gap-1 mb-2 text-sm font-medium text-gray-800 dark:text-white">
            Workspace Name
          </label>
          <input type="text" name="name" maxlength="${NAME_MAX_LENGTH}" 
            class="py-2.5 px-3 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
            placeholder='Give your workspace a name (e.g., "Marketing Team")' autofocus="" />
        </div>
        <div>
          <label for="description" class="block mb-2 text-sm font-medium text-gray-800 dark:text-white">
            Description (Optional)
          </label>
          <div class="max-w-full">
            <textarea name="description" maxlength="${DESCRIPTION_MAX_LENGTH}"
              class="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
              rows="3" placeholder="Briefly describe your workspace. What’s it for? (Max 160 characters)"></textarea>
              <p class="text-gray-400 text-xs text-right dark:text-neutral-500 mt-[2px]"><span class="ws-desc-count">00</span>/160</p>
          </div>
        </div>

        <div>
          <label for="website_url" class="flex gap-1 mb-2 text-sm font-medium text-gray-800 dark:text-white">
            Your Website (Optional)
          </label>
          <input type="text" name="website_url"
            class="py-2.5 px-3 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
            placeholder="Add your website link (if applicable)" />
        </div>
      </div>
      <!-- End Body -->

      <div class="flex justify-end items-center gap-x-2 py-3 px-4 border-t dark:border-neutral-700">
        <button id="btn-create-workspace-modal-save" type="submit"
          class="btn btn-sm btn-solid">
          <span class="btn-label flex gap-x-1 items-center">
          Create Workspace
          <span/>
        </button>
      </div>
    </form>
    `;
  }

  return { init, render, resetForm, resetFormErrors };
}